import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { fetchWithLocalCache } from "../lib/cache";

/* -------------------- TradingView chart (top) -------------------- */
/**
 * Uses exchange from URL (?ex=NYSE) OR from API row ("Exchange").
 * Default view: last year (12M)
 * Smaller iframe
 *
 * IMPORTANT: No conditional hooks (eslint-safe).
 */
function TVMiniChart({ symbol, exchange, height = 260, theme = "light" }) {
  const s = String(symbol || "").trim().toUpperCase();
  const ex = String(exchange || "").trim().toUpperCase();
  const colorTheme = theme === "dark" ? "dark" : "light";

  const tvSymbol = useMemo(() => {
    if (!s) return "";
    return ex ? `${ex}:${s}` : s;
  }, [s, ex]);

const src = useMemo(() => {
  if (!tvSymbol) return "";

  return `https://s.tradingview.com/widgetembed/?symbol=${encodeURIComponent(tvSymbol)}&interval=D&timeframe=12M&theme=${colorTheme}`;
}, [tvSymbol, colorTheme]);

  if (!tvSymbol || !src) return null;

  return (
    <div
      style={{
        width: "100%",
        border: "1px solid #eee",
        borderRadius: 14,
        overflow: "hidden",
        background: "#fff",
      }}
    >
      <iframe
        title={`tv-${tvSymbol}`}
        key={`${tvSymbol}-${height}-${colorTheme}`}
        src={src}
        style={{ width: "100%", height, border: 0, display: "block" }}
        loading="lazy"
      />
    </div>
  );
}

/* -------------------- responsive helper -------------------- */
function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

/* -------------------- key helpers -------------------- */
function normalizeKey(k) {
  return String(k || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[^\w %/.\-<=]/g, "");
}

function buildKeyIndex(obj) {
  const idx = new Map();
  if (!obj || typeof obj !== "object") return idx;
  for (const k of Object.keys(obj)) idx.set(normalizeKey(k), k);
  return idx;
}

function getAny(row, keyIndex, candidates) {
  for (const cand of candidates) {
    const real = keyIndex.get(normalizeKey(cand));
    if (real != null) {
      const v = row?.[real];
      if (v !== null && v !== undefined && String(v).trim() !== "") return v;
    }
  }
  return null;
}

/* -------------------- formatting -------------------- */
function formatNumberCompact(n) {
  if (n === null || n === undefined || n === "") return "";
  const num = Number(String(n).replace(/,/g, ""));
  if (!Number.isFinite(num)) return String(n);

  if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
  if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
  if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
  if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
  return num.toString();
}

function formatNumberCompactAbs(n) {
  if (n === null || n === undefined || n === "") return "";
  const num = Math.abs(Number(String(n).replace(/,/g, "")));
  if (!Number.isFinite(num)) return String(n);

  if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
  if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
  if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
  if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
  return num.toString();
}

function formatDecimal(v, decimals = 2) {
  if (v === null || v === undefined || v === "") return "";
  const num = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(num)) return String(v);
  return num.toFixed(decimals);
}

function formatPercent(v, decimals = 2) {
  if (v === null || v === undefined || v === "") return "";
  const num = Number(String(v).replace(/[% ,]/g, ""));
  if (!Number.isFinite(num)) return String(v);

  const asFraction = Math.abs(num) <= 1.5;
  const pct = asFraction ? num * 100 : num;
  return pct.toFixed(decimals) + "%";
}

function formatPercentAlready(v, decimals = 2) {
  if (v === null || v === undefined || v === "") return "";
  const num = Number(String(v).replace(/[% ,]/g, ""));
  if (!Number.isFinite(num)) return String(v);
  return num.toFixed(decimals) + "%";
}

function formatPercentDiv100(v, decimals = 2) {
  if (v === null || v === undefined || v === "") return "";
  const num = Number(String(v).replace(/[% ,]/g, ""));
  if (!Number.isFinite(num)) return String(v);
  return formatPercent(num / 100, decimals);
}

function formatPercentMul100(v, decimals = 2) {
  if (v === null || v === undefined || v === "") return "";
  const num = Number(String(v).replace(/[% ,]/g, ""));
  if (!Number.isFinite(num)) return String(v);
  return (num * 100).toFixed(decimals) + "%";
}

function renderValue(type, raw) {
  if (raw === null || raw === undefined || String(raw).trim() === "") return "—";
  if (type === "compact") return formatNumberCompact(raw) || "—";
  if (type === "compactAbs") return formatNumberCompactAbs(raw) || "—";
  if (type === "number2") return formatDecimal(raw, 2) || "—";
  if (type === "number4") return formatDecimal(raw, 4) || "—";
  if (type === "percent") return formatPercent(raw, 2) || "—";
  if (type === "percentAlready") return formatPercentAlready(raw, 2) || "—";
  if (type === "percentDiv100") return formatPercentDiv100(raw, 2) || "—";
  if (type === "percentMul100") return formatPercentMul100(raw, 2) || "—";
  return String(raw);
}

/* -------------------- sections + fields -------------------- */
const SECTIONS = [
  {
    title: "Basics",
    fields: [
      { label: "Company", type: "text", keys: ["Stock", "Company", "Name"] },
      { label: "Ticker", type: "text", keys: ["Symbol", "Ticker"] },
      { label: "Exchange", type: "text", keys: ["Exchange"] },
      { label: "Price", type: "number2", keys: ["Price", "Last", "Last Price"] },
      { label: "Employees", type: "compact", keys: ["Employees", "Employee Count"] },
      { label: "Volume", type: "number2", keys: ["Volume", "Vol"] },
    ],
  },
  {
    title: "Valuation",
    fields: [
      { label: "Market Cap", type: "compact", keys: ["Mkt Cap", "Market Cap", "MarketCap"] },
      { label: "Enterprise Value", type: "compact", keys: ["Enterprise Value", "EV", "Ent Value"] },
      { label: "Shares Outstanding", type: "compact", keys: ["Shares Outstanding", "Shares", "Shs Out", "SharesOut"] },
      { label: "P/B", type: "number2", keys: ["P/B", "PB", "Price/Book"] },
      { label: "Forward P/E", type: "number2", keys: ["Forward P/E", "Fwd P/E", "Forward PE", "FPE"] },
      { label: "PEG", type: "number2", keys: ["PEG"] },
      { label: "EV/Sales", type: "number2", keys: ["EV/Sales", "EV / Sales", "EV/S", "EV to Sales", "Enterprise Value/Revenue"] },
    ],
  },
  {
    title: "Margins & Ratings",
    fields: [
      { label: "Gross Margin", type: "percent", keys: ["Gross Margin", "GrossMargin"] },
      { label: "Operating Margin", type: "percent", keys: ["Operating Margin", "Op Margin", "OperatingMargin"] },
    ],
  },
  {
    title: "Profitability & Cash Generation",
    fields: [
      { label: "FCF Margin", type: "percentDiv100", keys: ["FCF Margin", "Free Cash Flow Margin"] },
      { label: "Dividend Yield", type: "percent", keys: ["Div", "Dividend", "Dividend Yield"] },
      { label: "Buyback Yield", type: "percent", keys: ["Buyback", "Buyback Yield"] },
      { label: "Revenue", type: "compact", keys: ["Revenue", "Sales", "Total Revenue"] },
      { label: "EPS", type: "number2", keys: ["EPS", "Earnings Per Share"] },
      { label: "Operating Cash Flow", type: "compact", keys: ["Operating Cash Flow", "Operating CF", "CFO"] },
      { label: "Capex", type: "compactAbs", keys: ["Capex", "Capital Expenditures", "Capital Expenditure"] },
      { label: "Free Cash Flow", type: "compact", keys: ["Free Cash Flow", "FCF"] },
    ],
  },
  {
    title: "Balance Sheet & Liquidity",
    fields: [
      { label: "Cash", type: "compact", keys: ["Cash", "Cash & Equivalents", "Cash and Equivalents"] },
      { label: "Debt", type: "compact", keys: ["Debt", "Total Debt", "TotalDebt"] },
      { label: "Equity", type: "compact", keys: ["Equity", "Total Equity", "Shareholders' Equity", "Shareholders Equity"] },
      { label: "Working Capital", type: "compact", keys: ["Working Capital", "WorkingCapital"] },
      { label: "Current Ratio", type: "number2", keys: ["Current Ratio", "Curr Ratio", "CurrentRatio"] },
    ],
  },
  {
    title: "Ownership & Short Interest",
    fields: [
      { label: "Short Shares", type: "percentMul100", keys: ["ShortShares", "Short Shares", "Shares Short", "Short Interest Shares"] },
      { label: "Inst Own", type: "percentMul100", keys: ["Inst Own", "Institutional Ownership", "Institutional Own"] },
    ],
  },
  {
    title: "Technical",
    fields: [
      { label: "RSI", type: "number2", keys: ["RSI"] },
      { label: "MA200", type: "number2", keys: ["ma200", "MA200", "200 MA", "200DMA"] },
      { label: "MA200 Change", type: "percent", keys: ["ma200ch", "MA200 Chg", "MA200 Change"] },
    ],
  },
  {
    title: "Risk & Distribution",
    fields: [
      { label: "Volatility (daily % stdev)", type: "number4", keys: ["Volatility (daily % stdev)", "Volatility", "Daily Volatility"] },
      { label: "Skewness (daily %)", type: "number4", keys: ["Skewness (daily %)", "Skewness", "Skew"] },
      { label: "Kurtosis (excess, daily %)", type: "number4", keys: ["Kurtosis (excess, daily %)", "Excess Kurtosis", "Kurtosis"] },
    ],
  },
  {
    title: "Next Year (Estimates)",
    fields: [
      { label: "Rv Next Year", type: "percent", keys: ["Rv Next Year", "Revenue Next Year", "Next Year Revenue", "Rev Next Year"] },
      { label: "EPS Next Year", type: "percent", keys: ["EPS Next Year", "Next Year EPS", "EPS NY"] },
    ],
  },
  {
    title: "SPY Behavior",
    fields: [
      { label: "% up when SPY down", type: "percentAlready", keys: ["% up when SPY down", "upspydown"] },
      { label: "% up when SPY down <= -1.5%", type: "percentAlready", keys: ["% up when SPY down <= -1.5%", "upspydown2"] },
    ],
  },
];

/* -------------------- news sorting helper -------------------- */
function toDateMs(n) {
  const d = n?.date ? Date.parse(String(n.date)) : NaN;
  if (Number.isFinite(d)) return d;

  const age = String(n?.age || "").toLowerCase().trim();
  const m = age.match(/(\d+)\s*(minute|min|hour|hr|day|week|month|year)s?\s*ago/);
  if (m) {
    const num = Number(m[1]);
    const unit = m[2];
    const mult =
      unit.startsWith("min") ? 60 * 1000 :
      unit.startsWith("hour") || unit.startsWith("hr") ? 60 * 60 * 1000 :
      unit.startsWith("day") ? 24 * 60 * 60 * 1000 :
      unit.startsWith("week") ? 7 * 24 * 60 * 60 * 1000 :
      unit.startsWith("month") ? 30 * 24 * 60 * 60 * 1000 :
      unit.startsWith("year") ? 365 * 24 * 60 * 60 * 1000 :
      0;
    return Date.now() - num * mult;
  }

  return 0;
}

export default function StockDetail() {
  const isMobile = useIsMobile(900);

  const params = useParams();
  const sym = String(params.symbol || params.ticker || params.id || "").trim().toUpperCase();

  // ✅ read exchange from URL: /stocks/LLY?ex=NYSE
  const [searchParams] = useSearchParams();
  const exParam = String(searchParams.get("ex") || "").trim().toUpperCase();

  const [row, setRow] = useState(null);

  const [news, setNews] = useState([]);
  const [loadingNews, setLoadingNews] = useState(true);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setRow(null);
    setError("");
  }, [sym]);

  /* -------------------- fetch stock detail (with fallback) -------------------- */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);

        const base = process.env.REACT_APP_SHEETS_API_URL;
        if (!base) throw new Error("Missing REACT_APP_SHEETS_API_URL (.env + restart)");
        const apiKey = process.env.REACT_APP_API_KEY;
        if (!apiKey) throw new Error("Missing REACT_APP_API_KEY (.env + restart)");
        if (!sym) throw new Error("Missing ticker in URL. Make sure your route is /stocks/:symbol");

        const detailUrlTicker = `${base}?type=stocks&view=detail&ticker=${encodeURIComponent(sym)}&key=${encodeURIComponent(apiKey)}`;
        const detailUrlSymbol = `${base}?type=stocks&view=detail&symbol=${encodeURIComponent(sym)}&key=${encodeURIComponent(apiKey)}`;

        async function fetchRecord(url, cacheKey) {
          const res = await fetchWithLocalCache(url, cacheKey, 5 * 60 * 1000);
          const payload = res?.data ?? res;
          if (payload?.error) throw new Error(payload.error);
          let record = payload?.row ?? payload;
          if (Array.isArray(record)) record = record[0] ?? null;
          return record && typeof record === "object" ? record : null;
        }

        let record = await fetchRecord(detailUrlTicker, `stock_detail_${sym}_ticker`);
        if (!record) record = await fetchRecord(detailUrlSymbol, `stock_detail_${sym}_symbol`);

        const returnedSymbol = String(record?.Symbol ?? record?.Ticker ?? "").trim().toUpperCase();
        const looksWrong = record && returnedSymbol && returnedSymbol !== sym;

        if (!record || looksWrong) {
          const listUrl = `${base}?type=stocks&view=list&key=${encodeURIComponent(apiKey)}`;
          const res = await fetchWithLocalCache(listUrl, "stocks_list_for_detail_v1", 10 * 60 * 1000);
          const payload = res?.data ?? res;
          if (payload?.error) throw new Error(payload.error);

          const list = Array.isArray(payload) ? payload : [];
          const hit =
            list.find((r) => String(r?.Symbol ?? r?.Ticker ?? "").trim().toUpperCase() === sym) || null;
          record = hit;
        }

        if (!cancelled) setRow(record);
      } catch (e) {
        if (!cancelled) setError(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sym]);

  const keyIndex = useMemo(() => buildKeyIndex(row), [row]);

  const companyName = useMemo(() => {
    const raw = getAny(row, keyIndex, ["Stock", "Company", "Name"]);
    return String(raw || "").trim();
  }, [row, keyIndex]);

  const exchangeFromRow = useMemo(() => {
    const raw = getAny(row, keyIndex, ["Exchange"]);
    return String(raw || "").trim().toUpperCase();
  }, [row, keyIndex]);

  // ✅ prefer URL exchange, then row exchange
  const effectiveExchange = exParam || exchangeFromRow || "";

  function getLogoUrl(ticker) {
    const s = String(ticker || "").trim().toUpperCase();
    return s ? `https://financialmodelingprep.com/image-stock/${encodeURIComponent(s)}.png` : "";
  }

  const logoUrl = useMemo(() => getLogoUrl(sym), [sym]);

  const sectionsResolved = useMemo(() => {
    return SECTIONS.map((sec) => {
      const items = sec.fields.map((f) => {
        const raw = getAny(row, keyIndex, f.keys);
        return {
          ...f,
          raw,
          display: f.type === "text" ? (raw ? String(raw) : "—") : renderValue(f.type, raw),
        };
      });
      const hasAny = items.some((it) => it.raw !== null && it.raw !== undefined && String(it.raw).trim() !== "");
      return { title: sec.title, items, show: hasAny };
    });
  }, [row, keyIndex]);

  /* -------------------- Google articles (your API route) -------------------- */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoadingNews(true);
        setNews([]);

        if (!sym) return;

        const r = await fetch(`/api/googleNews?symbol=${encodeURIComponent(sym)}&limit=5`);
        const j = await r.json();
        if (!r.ok) throw new Error(j?.error || "Failed to load Google articles");

        const items = Array.isArray(j?.items) ? j.items : [];
        items.sort((a, b) => toDateMs(b) - toDateMs(a));

        if (!cancelled) setNews(items.slice(0, 5));
      } catch {
        if (!cancelled) setNews([]);
      } finally {
        if (!cancelled) setLoadingNews(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sym]);

  const googleNewsUrl = `https://news.google.com/search?q=${encodeURIComponent(`${sym} stock`)}&hl=en-US&gl=US&ceid=US:en`;

  const chartHeight = isMobile ? 230 : 260;

  // slightly smaller / tighter article styling
  const ARTICLE_TITLE_SIZE = 13;
  const ARTICLE_META_SIZE = 11;
  const ARTICLE_SNIP_SIZE = 12;
  const ARTICLE_PAD = isMobile ? 9 : 10;
  const ARTICLE_GAP = 8;

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: isMobile ? 14 : 24, fontFamily: "system-ui" }}>
      {/* Header with logo + name */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <img
            src={logoUrl}
            alt=""
            width={isMobile ? 26 : 30}
            height={isMobile ? 26 : 30}
            style={{ borderRadius: 8, display: "block" }}
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />

          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              <h2 style={{ margin: 0, lineHeight: 1.1 }}>{sym || "Stock"}</h2>
              {companyName ? (
                <span style={{ color: "#666", fontWeight: 650, fontSize: 14, overflowWrap: "anywhere" }}>
                  {companyName}
                </span>
              ) : null}
              {effectiveExchange ? (
                <span style={{ color: "#888", fontWeight: 700, fontSize: 12 }}>
                  {effectiveExchange}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div style={{ marginLeft: "auto", display: "flex", gap: 12, fontSize: 14, flexWrap: "wrap" }}>
          <Link to="/stocks" style={{ textDecoration: "none" }}>Stocks</Link>
          <Link to="/etfs" style={{ textDecoration: "none" }}>ETFs</Link>
          <Link to="/feed" style={{ textDecoration: "none" }}>Feed</Link>
        </div>
      </div>

      {/* CHART AT TOP */}
      <div style={{ marginTop: 12 }}>
        <TVMiniChart symbol={sym} exchange={effectiveExchange} height={chartHeight} theme="light" />
      </div>

      {/* Articles */}
      <div style={{ marginTop: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Recent Articles</h3>
          <a
            href={googleNewsUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              marginLeft: "auto",
              fontSize: 12,
              textDecoration: "none",
              border: "1px solid #eee",
              borderRadius: 999,
              padding: "5px 9px",
              background: "#fafafa",
              color: "#222",
            }}
            title="Open Google News search"
          >
            Open Google News →
          </a>
        </div>

        {loadingNews && <div style={{ marginTop: 8, color: "#666", fontSize: 12 }}>Loading articles…</div>}

        {!loadingNews && news.length === 0 && (
          <div style={{ marginTop: 8, color: "#666", fontSize: 12 }}>
            No articles loaded. (If you haven’t added <code>/api/googleNews</code> yet, use the button above.)
          </div>
        )}

        {!loadingNews && news.length > 0 && (
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: ARTICLE_GAP }}>
            {news.slice(0, 5).map((n, idx) => (
              <a
                key={idx}
                href={n.link}
                target="_blank"
                rel="noreferrer"
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  border: "1px solid #eee",
                  borderRadius: 12,
                  padding: ARTICLE_PAD,
                  background: "#fff",
                  display: "block",
                }}
              >
                <div style={{ fontWeight: 850, lineHeight: 1.25, fontSize: ARTICLE_TITLE_SIZE }}>
                  {n.title}
                </div>

                {(n.source || n.age || n.date) ? (
                  <div style={{ marginTop: 5, color: "#777", fontSize: ARTICLE_META_SIZE }}>
                    {n.source ? n.source : ""}
                    {n.source && (n.age || n.date) ? " · " : ""}
                    {n.age ? n.age : ""}
                    {n.age && n.date ? " · " : ""}
                    {n.date ? n.date : ""}
                  </div>
                ) : null}

                {n.snippet ? (
                  <div style={{ marginTop: 6, color: "#444", fontSize: ARTICLE_SNIP_SIZE, lineHeight: 1.35 }}>
                    {String(n.snippet).length > 190 ? String(n.snippet).slice(0, 190) + "…" : n.snippet}
                  </div>
                ) : null}
              </a>
            ))}
          </div>
        )}
      </div>

      {loading && <div style={{ marginTop: 12 }}>Loading…</div>}
      {error && <div style={{ marginTop: 12, color: "crimson" }}>Error: {error}</div>}

      {/* Metrics sections */}
      {!loading && !error && row && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 14 }}>
          {sectionsResolved.filter((s) => s.show).map((sec) => (
            <div key={sec.title} style={{ border: "1px solid #eee", borderRadius: 14, overflow: "hidden" }}>
              <div style={{ padding: 12, background: "#fafafa", borderBottom: "1px solid #eee", fontWeight: 800 }}>
                {sec.title}
              </div>

              <div style={{ padding: isMobile ? 10 : 12 }}>
                <div
                  style={{
                    display: "grid",
                    gap: 10,
                    alignItems: "start",
                    gridTemplateColumns: isMobile ? "1fr" : "320px 1fr",
                  }}
                >
                  {sec.items.map((it) => (
                    <React.Fragment key={it.label}>
                      <div
                        style={{
                          color: "#666",
                          fontSize: isMobile ? 12 : 13,
                          fontWeight: isMobile ? 700 : 400,
                          marginTop: isMobile ? 6 : 0,
                        }}
                      >
                        {it.label}
                      </div>

                      {!isMobile ? (
                        <div style={{ fontSize: 14, fontWeight: 650, overflowWrap: "anywhere" }}>
                          {it.display}
                        </div>
                      ) : (
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 650,
                            paddingBottom: 6,
                            borderBottom: "1px dashed #eee",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {it.display}
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && !row && (
        <div style={{ marginTop: 12, color: "#666" }}>
          No details found for {sym}.
        </div>
      )}
    </div>
  );
}
